import React, { useState } from 'react';

import {
  Button,
  Checkbox,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { useSaveSettings, VectorTile, WMSLayerInfo } from '@ais-ui/websockets';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as z from 'zod';
import { getCapabilities, LayerInfo } from '../../WMSLayers/getCapabilities';
import {
  addWmsToSettings,
  UpdateWmsToSettings,
} from '../../WMSLayers/useWMSSettings';
import { useSettings } from '../SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { cn } from '@ais-ui/utils';
import { PiGlobeHemisphereEastThin, PiGridFourThin } from 'react-icons/pi';
import {
  addVectorTileToSettings,
  UpdateVectorTilesToSettings,
} from '../../VectorTiles/useVectorTileSettings';

const addVectorTileFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  url: z
    .string()
    .min(1, 'URL is required')
    .url('Must be a valid URL')
    .refine((url) => {
      // Check if URL ends with /tiles/, /vector/, or similar pattern
      return /\/(tiles|vector|mvt|vectortiles)\/?\s*$/.test(url);
    }, 'URL must end with a vector tile endpoint (e.g., /tiles/)'),
});

type AddVectorTileFormValues = z.infer<typeof addVectorTileFormSchema>;

const defaultValues: Partial<AddVectorTileFormValues> = {
  name: '',
  url: '',
};

type CreateModeProps = {
  mode: 'create';
  existingVectorTile?: undefined;
  className?: string;
};

type UpdateModeProps = {
  mode: 'edit';
  existingVectorTile: VectorTile;
  className?: string;
};

type VectorTileCreateUpdateServiceProps = CreateModeProps | UpdateModeProps;

interface ExampleVectorTilesProps {
  onChange: (url: string) => void;
}

const ExampleVectorTiles = ({ onChange }: ExampleVectorTilesProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const exampleServices = [
    {
      name: 'OSM undersea cables',
      url: 'https://openinframap.org/tiles/',
    },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="icon" variant="outline">
          <PiGridFourThin className="h-7 w-7"></PiGridFourThin>
        </Button>
      </PopoverTrigger>
      <PopoverContent>
        <Command className="space-y-2">
          <CommandInput placeholder="Find Vector Tile..." />
          <CommandList className="space-y-1">
            <CommandGroup heading="Vector Tiles">
              {exampleServices.map((service) => (
                <CommandItem
                  key={service.name}
                  onSelect={() => {
                    onChange(service.url);
                    setOpen(false);
                  }}
                >
                  {service.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const VectorTileAddEditService = (
  props: VectorTileCreateUpdateServiceProps
) => {
  const { messages } = useLocalization();
  const { mode, existingVectorTile } = props;
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  let initialValues = defaultValues;
  if (mode === 'edit') {
    initialValues = {
      name: existingVectorTile.name,
      url: existingVectorTile.url,
    };
  }

  const form = useForm<AddVectorTileFormValues>({
    resolver: zodResolver(addVectorTileFormSchema),
    defaultValues: initialValues,
  });

  const { toast } = useToast();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();

  async function onUpdateService(data: AddVectorTileFormValues) {
    if (mode !== 'edit') {
      return;
    }
    setIsSubmitting(true);

    existingVectorTile.name = data.name;
    existingVectorTile.url = data.url;

    const settingsToSave = UpdateVectorTilesToSettings(
      existingVectorTile,
      settings
    );

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        setShowDialog(false);
        toast({
          className: 'update-vector-tile-service-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsVectorTileSuccessUpdateMessage,
        });
        setIsSubmitting(false);
      },
      onError: () => {
        setShowDialog(false);
        toast({
          className: 'update-vector-tile-layer-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsVectorTileFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  async function onAddService(data: AddVectorTileFormValues) {
    if (mode !== 'create') {
      return;
    }
    setIsSubmitting(true);
    const vectorTile: VectorTile = {
      id: uuidv4(),
      name: data.name,
      url: data.url,
      enabled: true,
    };

    const settingsToSave = addVectorTileToSettings(vectorTile, settings);

    mutateSettings(settingsToSave, {
      onSuccess: () => {
        setShowDialog(false);
        toast({
          className: 'add-vector-tile-service-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsVectorTileSuccessAddMessage,
        });
        setIsSubmitting(false);
      },
      onError: (error) => {
        setShowDialog(false);
        toast({
          className: 'set-vector-tile-layer-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsVectorTileFailedAddMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button
          variant={mode === 'edit' ? 'secondary' : 'default'}
          className={cn(mode === 'edit' && 'text-primary', props.className)}
        >
          {mode === 'create'
            ? messages.settingsVectorTileAddButtonText
            : messages.settingsVectorTileEditButtonText}
        </Button>
      </DialogTrigger>
      <DialogContent id="add-update-service-dialog">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? messages.settingsVectorTileAddTitleText
              : messages.settingsVectorTileEditTitleText}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? messages.settingsVectorTileAddSubtitleText
              : messages.settingsVectorTileEditSubtitleText}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={
              mode === 'create'
                ? form.handleSubmit(onAddService)
                : form.handleSubmit(onUpdateService)
            }
            className="space-y-8"
          >
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {messages.settingsVectorTileNameLabel} *
                    </FormLabel>
                    <FormControl onChange={() => form.clearErrors()}>
                      <Input
                        type="text"
                        placeholder={messages.settingsVectorTileNamePlaceholder}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex justify-between w-full">
                      <FormLabel>
                        {messages.settingsVectorTileUrlLabel} *
                      </FormLabel>
                      <InfoIconTooltip>
                        Vector tile endpoint URL should end with /tiles/,
                        /vector/, or similar path
                      </InfoIconTooltip>
                    </div>
                    <FormControl onChange={() => form.clearErrors()}>
                      <div className="flex justify-between space-x-4">
                        <Input
                          type="text"
                          placeholder={
                            messages.settingsVectorTileUrlPlaceholder
                          }
                          {...field}
                        />
                        <ExampleVectorTiles onChange={field.onChange} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <DialogFooter className="p-4">
              <Button
                id="wms-confirm-add-button"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? messages.genericSubmitting
                  : messages.genericSubmit}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
